import { ChartFrame } from 'components/ChartFrame'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useElementSize, useWindowSize } from 'usehooks-ts'
import { scaleLinear, scaleTime, extent } from 'd3'
import { global_locales, virus_colors } from 'components/constants'
import { useDate } from 'Globals/useDate'

const xTicksDesktop = [
  '2023-07-01',
  '2024-01-01',
  '2024-07-01',
  '2025-01-01',
  '2025-07-01',
  '2026-01-01',
  '2026-07-01'
]
const xTicksMobile = ['2024-01-01', '2025-01-01', '2026-01-01']
const svgHeight = 400

const locales = global_locales.covidovertime
const start_date = '2023-07-01'
const round_y_max_to = 1000
export const CovidOverTime = ({
  colorMode = 'light',
  lang = 'de'
}: {
  colorMode?: 'dark' | 'light' | undefined
  lang?: 'de' | 'fr'
}) => {
  const [data, setData] = useState<any[] | undefined>()
  const [rawData, setRawData] = useState<any[] | undefined>([])
  const [yMax, setYMax] = useState<number>(0)
  const [yTicks, setYTicks] = useState<number[]>([0, 500, 1000, 1500, 2000])
  const [containerWidth, setContainerWidth] = useState<number>(300)
  const [containerHeight, setContainerHeight] = useState<number>(svgHeight)
  const [containerRef, { width, height }] = useElementSize()
  const svgRef = useRef<SVGSVGElement>(null)

  const [toolTipData, setToolTipData] = useState<TooltipData | undefined>(
    undefined
  )
  const endDate = useDate()

  const windowSize = useWindowSize()
  const xTicks = windowSize.width < 712 ? xTicksMobile : xTicksDesktop
  //

  useEffect(() => {
    setContainerWidth(width || 100)
  }, [width])

  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard-2023/covid_detection.json'
    )
      .then(a => a.json())
      .then(a => {
        let relevantdata = a.filter((f: any) => {
          return new Date(f.end_date) >= new Date(start_date)
        })
        let ext = extent(relevantdata, (d: any) => d.detections)
        var max = ext[1] ? +ext[1] : 2000
        setYMax(Math.ceil(max / round_y_max_to) * round_y_max_to)
        const yTicks = []
        for (let i = 1; i <= Math.ceil(max / round_y_max_to); i++) {
          yTicks.push(i * round_y_max_to)
        }
        setYTicks(yTicks)
        setRawData(relevantdata)
        setData(relevantdata)
      })
  }, [])

  const drawArea = useMemo(
    () => ({
      top: 30,
      left: 50,
      right: containerWidth,
      bottom: containerHeight - 30
    }),
    [containerWidth, containerHeight, data]
  )

  const x = useMemo(() => {
    return scaleTime()
      .domain([new Date(start_date), new Date()])
      .range([drawArea.left, drawArea.right])
  }, [drawArea.left, drawArea.right, endDate])

  const y = useMemo(() => {
    return scaleLinear()
      .domain([yMax, 0])
      .range([drawArea.bottom, drawArea.top])
  }, [drawArea.bottom, drawArea.top, yMax])

  function prepTooltipData (e: any) {
    const chartx = e.clientX ? e.clientX : e.touches[0].clientX

    var date = new Date(
      x.invert(
        chartx -
          (svgRef.current ? svgRef.current?.getBoundingClientRect().x : 0)
      )
    )
      .toISOString()
      .split('T')[0]

    const dataelement = rawData?.find(
      a =>
        new Date(a.end_date) >= new Date(date) &&
        new Date(a.start_date) <= new Date(date)
    )

    if (dataelement) {
      setToolTipData({ x: dataelement.end_date, date, day: dataelement })
    }
  }

  return (
    <ChartFrame
      lang={lang}
      title={locales[lang].title}
      timestamp={
        <>
          {new Date(endDate).toLocaleDateString(lang + '-CH', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </>
      }
      lead={locales[lang].lead}
      source={locales[lang].source}
      colorMode={colorMode}
    >
      <VirusOverTimeRoot ref={containerRef}>
        <SVG
          colorMode={colorMode}
          ref={svgRef}
          width={'100%'}
          height={containerHeight + 'px'}
          onMouseMove={prepTooltipData}
          onTouchMove={prepTooltipData}
          onMouseOut={() => {
            setToolTipData(undefined)
          }}
        >
          <g className='xTicks'>
            {xTicks.map((date, i) => {
              return (
                <g
                  transform={`translate(${x(new Date(date))},${
                    drawArea.bottom
                  })`}
                >
                  <line y1={10} y2={0} stroke='var(--text-color)' />

                  <text
                    textAnchor={i === xTicks.length - 1 ? 'middle' : 'middle'}
                    y={26}
                    fill={colorMode === 'dark' ? 'white' : '#202346'}
                  >
                    {new Date(date).toLocaleDateString(lang + '-CH', {
                      month: windowSize.width < 712 ? 'numeric' : 'short',
                      year: 'numeric'
                    })}
                  </text>
                </g>
              )
            })}
          </g>
          <g className='yTicks'>
            {yTicks.map(val => {
              return (
                <g transform={`translate(0,${drawArea.bottom - y(val) + 30})`}>
                  <line x2={drawArea.left - 4} stroke='var(--text-color)' />
                  <line
                    x2={drawArea.left - 4}
                    x1={drawArea.right}
                    stroke='var(--text-color)'
                    opacity={val === 0 ? 1 : 0.1}
                  />
                  <text
                    x={drawArea.left - 4}
                    y={16}
                    textAnchor='end'
                    fill={colorMode === 'dark' ? 'white' : '#202346'}
                  >
                    {val}
                  </text>
                </g>
              )
            })}
          </g>
          {data &&
            data.map((d: any) => {
              return (
                <rect
                  x={x(new Date(d.start_date))}
                  y={drawArea.bottom - y(d.detections) + drawArea.top}
                  height={y(d.detections) - drawArea.top}
                  width={
                    ((drawArea.right - drawArea.left) / data.length) * 0.85
                  }
                  fill={virus_colors['sars-cov-2']}
                />
              )
            })}
          <line
            x1={0}
            x2={containerWidth}
            y1={drawArea.bottom}
            y2={drawArea.bottom}
            stroke='var(--text-color)'
          />
          {toolTipData && data && (
            <Tooltip
              lang={lang}
              colorMode={colorMode}
              data={toolTipData}
              drawArea={drawArea}
              width={width || 100}
              x={
                x(new Date(toolTipData.day.start_date)) +
                (((drawArea.right - drawArea.left) / data.length) * 0.85) / 2
              }
              y={drawArea.bottom - y(toolTipData.day.detections) + drawArea.top}
            />
          )}
        </SVG>
      </VirusOverTimeRoot>
    </ChartFrame>
  )
}

type TooltipData = {
  x: string
  date: string
  day: any
}

const Tooltip = ({
  data,
  drawArea,
  width,
  x,
  y,
  colorMode,
  lang
}: {
  data: TooltipData
  drawArea: {
    top: number
    left: number
    right: number
    bottom: number
  }
  width: number
  x: number
  y: number
  colorMode: 'dark' | 'light' | undefined
  lang: 'de' | 'fr'
}) => {
  const { day } = data
  // if(!day) return null
  const date_width = 200
  const direction: 'left' | 'right' = x > width * 0.5 ? 'right' : 'left'

  const align =
    direction === 'left' ? 'left' : direction === 'right' ? 'right' : 'center'

  return (
    <g className='tooltip' transform={`translate(${x},${drawArea.top - 2})`}>
      <foreignObject
        x={
          direction === 'left'
            ? 10
            : direction === 'right'
            ? -date_width - 10
            : 10
        }
        y={0}
        width={date_width}
        height={56}
      >
        <body>
          <div
            style={{
              textAlign: align,
              paddingTop: '5px'
            }}
          >
            <div className='element'>
              <TTDate
                style={{
                  textAlign: align
                }}
              >
                {new Date(day.start_date).toLocaleDateString(lang + '-CH', {
                  day: '2-digit',
                  month: 'short'
                  // year: 'numeric',
                }) +
                  ' – ' +
                  new Date(
                    addDays(day.end_date, 3).toISOString().split('T')[0]
                  ).toLocaleDateString(lang + '-CH', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
              </TTDate>
              <VirusValue
                style={{
                  textAlign: align
                }}
              >
                {day ? day.detections : 0}
              </VirusValue>
            </div>
          </div>
        </body>
      </foreignObject>
      <rect
        width={2}
        height={y - 40}
        fill={colorMode === 'dark' ? 'rgba(255,255,255,.75)' : 'rgba(0,0,0,.5)'}
      />
    </g>
  )
}
const TTDate = styled.div`
  display: inline-block;
  flex: 0 0 100%;
  font-weight: 300;
  margin-bottom: 10px;
`

const VirusValue = styled.div`
  flex: 0 0 100%;
  font-weight: 700;
  font-size: 18px;
`

const VirusOverTimeRoot = styled.div``

const SVG = styled.svg<{ colorMode: 'dark' | 'light' | undefined }>`
  /* * {
    pointer-events: none;
  } */
  foreignObject {
    font-weight: 300;
    letter-spacing: 0.4px;
    font-size: 13px;
    line-height: 1em;
    color: var(--text-color);
    /* -webkit-box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.1); */
    /* box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.1); */
    /* background-color: var(--site-background); */
    div.element {
      display: flex;
      flex-wrap: wrap;
      border-bottom: ${props =>
        props.colorMode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.2)'
          : '1px solid rgba(255, 255, 255, 0.3)'};
      text-align: left;
      padding: 4px;
      padding-left: 8px;
      &:last-child {
        border-bottom: none;
      }
    }
  }

  .xTicks,
  .yTicks {
    font-size: 12px;
  }

  .tooltip {
    font-size: 14px;
  }
`

function addDays (date: string, days: number) {
  var result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}
